/**
 * Plan Page
 *
 * The entry point for the natural-language planning flow (FLOW-547). Three
 * states in one focused column: describe an initiative, answer the clarifying
 * questions the intake engine compiled, then read the assembled spec.
 *
 * The thread is an intake session, not a chat — its transcript is stored
 * against a conversation the Chat page deliberately does not list.
 */

import {
  Component,
  For,
  Show,
  Switch,
  Match,
  createResource,
  createSignal,
} from "solid-js";
import { useSearchParams } from "@solidjs/router";
import {
  Button,
  Dropdown,
  DropdownItem,
  Modal,
  Spinner,
} from "../components/ui";
import {
  IntakeComposer,
  IntakeThread,
  InitiativeSpecPanel,
  BreakdownPanel,
  NegotiationThread,
  CommittedItemsSummary,
  CapturedDecisionsPanel,
  DecisionQuickCapture,
} from "../components/plan";
import { useIntake, useBreakdown, useDecisions } from "../hooks";
import { useProjectId, useProjectPrefix } from "../contexts/ProjectContext";
import { getWorkItem } from "../api/workItems";
import { epicSeedText } from "../utils/planSeed";
import { getDisplayId } from "../utils/displayId";
import {
  committedSummary,
  setCommittedSummary,
} from "../stores/planCommitStore";
import type { InitiativeIntake } from "../api/types";

const STEPS = [
  {
    title: "Describe",
    description:
      "Say what you want to build in plain language — no ticket format required.",
  },
  {
    title: "Ground",
    description:
      "Flowicity reads any repo you've linked, your documents, and your backlog to anchor the breakdown in what already exists.",
  },
  {
    title: "Review",
    description:
      "Adjust the proposed items, then send the ones you keep straight to the backlog.",
  },
];

const PlanIcon: Component = () => (
  <svg
    class="w-12 h-12 mx-auto"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="1.5"
      d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z"
    />
  </svg>
);

/**
 * FLOW-641: the two ways out of a breakdown review. Both are confirmed — a
 * breakdown is minutes of grounded AI work plus whatever negotiation followed
 * it, and neither exit should be able to lose that to one stray click.
 *
 * They differ in what they cost. Discarding deletes the proposal and keeps the
 * spec; leaving keeps both and just closes the surface.
 */
type AbandonAction = "discard" | "leave";

const ABANDON_COPY: Record<
  AbandonAction,
  { title: string; body: string; confirm: string; cancel: string }
> = {
  discard: {
    title: "Discard this breakdown?",
    body: "The proposed epic and stories are deleted, and nothing is added to your backlog. The spec stays, so you can generate a new breakdown from it.",
    confirm: "Discard it",
    cancel: "Keep it",
  },
  leave: {
    title: "Leave this breakdown?",
    body: "Nothing has been added to your backlog yet. The breakdown is kept — reopen this session under “Past sessions” to come back to it.",
    confirm: "Leave it",
    cancel: "Stay here",
  },
};

const sessionLabel = (session: InitiativeIntake): string => {
  const spec = session.spec;
  const text = spec?.title ?? session.initiative_text;
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
};

export const PlanPage: Component = () => {
  const projectId = useProjectId();
  const prefix = useProjectPrefix();

  // FLOW-614: /plan?epic=<id> opens the composer pre-filled from that epic
  // rather than blank. Restoring the project's pending session would hide the
  // composer entirely, so seeding suppresses it — the session stays listed
  // under "Past sessions".
  //
  // FLOW-615: the same epic is sent as the session's target, so the breakdown
  // attaches its stories to it instead of proposing a sibling. A seed the
  // fetch never returned pins nothing — the composer is blank in that case
  // too, and a blank composer must start an ordinary untargeted session.
  const [searchParams] = useSearchParams<{ epic?: string }>();
  const epicId = () => searchParams.epic;
  const [seedResource] = createResource(epicId, getWorkItem);
  const [seedDismissed, setSeedDismissed] = createSignal(false);
  // Reading the resource rethrows a failed fetch, and a missing epic must not
  // take the page down with it — fall back to the blank composer instead.
  const seedEpic = () => (seedResource.error ? undefined : seedResource());
  // The composer reads initialValue once at mount, so hold it back until the
  // fetch has settled either way.
  const seedSettled = () => !epicId() || !seedResource.loading;
  const seedText = () => {
    const epic = seedEpic();
    return epic ? epicSeedText(epic) : undefined;
  };

  const intake = useIntake(projectId, () => !epicId());

  const questions = () => intake.intake()?.questions ?? [];
  const isReady = () => intake.intake()?.status === "ready";

  const breakdown = useBreakdown(() =>
    isReady() ? intake.intake()?.id : undefined,
  );
  // The two-pane review surface needs room the single-column intake flow
  // doesn't; every other state keeps the narrower, conversation-width column.
  const isReviewing = () => isReady() && Boolean(breakdown.breakdown());

  // FLOW-641: which exit the user asked for, while its confirmation is open.
  const [abandoning, setAbandoning] = createSignal<AbandonAction | null>(null);

  // A generated breakdown nobody has committed is the work worth guarding.
  // Once it is committed the items exist in the backlog and leaving costs
  // nothing, so that exit stays a single click.
  const hasUncommittedBreakdown = () => {
    const row = breakdown.breakdown();
    return Boolean(row && !row.committed_at);
  };

  const startAnother = () => {
    if (hasUncommittedBreakdown()) {
      setAbandoning("leave");
      return;
    }
    intake.reset();
  };

  const confirmAbandon = async () => {
    const action = abandoning();
    setAbandoning(null);
    if (action === "discard") {
      await breakdown.discard();
    } else if (action === "leave") {
      intake.reset();
    }
  };

  const decisions = useDecisions(
    () => intake.intake()?.id,
    () => Boolean(breakdown.breakdown()?.committed_at),
  );

  return (
    <div class="h-full flex flex-col">
      {/* Header */}
      <div class="px-4 py-4 border-b border-line bg-surface flex items-start justify-between gap-4">
        <div>
          <h1 class="text-lg font-semibold text-ink">Plan</h1>
          <p class="text-sm text-ink-3 mt-0.5">
            Turn intent into grounded work items
          </p>
        </div>

        <Dropdown
          class="shrink-0"
          align="right"
          trigger={
            <Button variant="secondary" size="sm">
              Past sessions
            </Button>
          }
        >
          <div class="w-80 max-h-80 overflow-y-auto">
            <Show
              when={intake.sessions()?.length}
              fallback={
                <p class="px-3 py-2 text-sm text-ink-3">
                  No planning sessions yet
                </p>
              }
            >
              <For each={intake.sessions()}>
                {(session) => (
                  <DropdownItem onClick={() => void intake.resume(session.id)}>
                    <span class="block truncate">{sessionLabel(session)}</span>
                    <span class="block text-xs text-ink-3 mt-0.5">
                      {session.status === "ready" ? "Complete" : "In progress"}
                    </span>
                  </DropdownItem>
                )}
              </For>
            </Show>
          </div>
        </Dropdown>
      </div>

      <main class="flex-1 overflow-auto">
        <div
          class={
            "mx-auto px-4 py-10 sm:py-14 " +
            (isReviewing() ? "max-w-6xl" : "max-w-3xl")
          }
        >
          <Show when={intake.error()}>
            {(message) => (
              <div
                role="alert"
                class="mb-6 flex items-start justify-between gap-4 rounded border border-signal-red/25 bg-signal-red-tint px-4 py-3"
              >
                <p class="text-sm text-signal-red">{message()}</p>
                <button
                  type="button"
                  class="text-sm font-medium text-signal-red hover:text-ink"
                  onClick={intake.clearError}
                >
                  Dismiss
                </button>
              </div>
            )}
          </Show>

          <Switch>
            {/* Empty state — the invitation to describe an initiative */}
            <Match when={!intake.intake()}>
              <div class="text-center">
                <div class="text-ink-3">
                  <PlanIcon />
                </div>
                <h2 class="mt-4 text-lg font-medium text-ink">
                  Describe what you want to build
                </h2>
                <p class="mt-1 mx-auto max-w-xl text-sm text-ink-3">
                  Plan turns a plain-language description into work items
                  grounded in any repo you've linked, your documents, and your
                  existing backlog — so the breakdown reflects what's actually
                  there.
                </p>
              </div>

              <Show when={seedEpic()}>
                {(epic) => (
                  <p class="mt-6 text-center text-sm text-ink-3">
                    {`Seeded from ${getDisplayId(
                      prefix(),
                      epic().id,
                      epic().item_number,
                    )} — ${epic().title}`}
                  </p>
                )}
              </Show>

              <Show when={seedResource.error && !seedDismissed()}>
                <div
                  role="alert"
                  class="mt-6 flex items-start justify-between gap-4 rounded border border-signal-amber/25 bg-signal-amber-tint px-4 py-3"
                >
                  <p class="text-sm text-signal-amber">
                    Couldn't load that epic — describe the work yourself.
                  </p>
                  <button
                    type="button"
                    class="text-sm font-medium text-signal-amber hover:text-ink"
                    onClick={() => setSeedDismissed(true)}
                  >
                    Dismiss
                  </button>
                </div>
              </Show>

              <div class="mt-8">
                <Show
                  when={seedSettled()}
                  fallback={
                    <div class="flex items-center justify-center gap-3 text-sm text-ink-3">
                      <Spinner size="sm" />
                      Loading the epic…
                    </div>
                  }
                >
                  <IntakeComposer
                    hero
                    autofocus
                    disabled={intake.busy()}
                    submitLabel="Start planning"
                    initialValue={seedText()}
                    placeholder="I want to add usage-based billing so teams pay for what they actually use…"
                    onSubmit={(text) => intake.start(text, seedEpic()?.id)}
                  />
                </Show>
              </div>

              <Show when={intake.busy()}>
                <div class="mt-6 flex items-center justify-center gap-3 text-sm text-ink-3">
                  <Spinner size="sm" />
                  Working out what still needs deciding…
                </div>
              </Show>

              <div class="mt-12 grid gap-3 sm:grid-cols-3">
                <For each={STEPS}>
                  {(step) => (
                    <div class="rounded border border-line bg-surface p-4">
                      <h3 class="text-sm font-medium text-ink">{step.title}</h3>
                      <p class="mt-1 text-sm text-ink-3">{step.description}</p>
                    </div>
                  )}
                </For>
              </div>
            </Match>

            {/* Intake complete — the assembled spec, then the breakdown review */}
            <Match when={isReady()}>
              <Show when={intake.intake()?.spec}>
                {(spec) => <InitiativeSpecPanel spec={spec()} />}
              </Show>

              <Show when={breakdown.error()}>
                {(message) => (
                  <div
                    role="alert"
                    class="mt-6 flex items-start justify-between gap-4 rounded border border-signal-red/25 bg-signal-red-tint px-4 py-3"
                  >
                    <p class="text-sm text-signal-red">{message()}</p>
                    <button
                      type="button"
                      class="text-sm font-medium text-signal-red hover:text-ink"
                      onClick={breakdown.clearError}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </Show>

              <Show
                when={breakdown.checked()}
                fallback={
                  <div class="mt-8 flex items-center justify-center gap-3 text-sm text-ink-3">
                    <Spinner size="sm" />
                    Checking for an existing breakdown…
                  </div>
                }
              >
                <Show
                  when={breakdown.breakdown()}
                  fallback={
                    <div class="mt-8 text-center">
                      <p class="mx-auto max-w-xl text-sm text-ink-3">
                        Turn this spec into a reviewable epic and stories,
                        grounded in any repo you've linked, your documents, and
                        your backlog.
                      </p>
                      <Button
                        variant="primary"
                        class="mt-4"
                        disabled={breakdown.busy()}
                        onClick={() => void breakdown.generate()}
                      >
                        Generate breakdown
                      </Button>
                      <Show when={breakdown.busy()}>
                        <div class="mt-6 flex items-center justify-center gap-3 text-sm text-ink-3">
                          <Spinner size="sm" />
                          Grounding a breakdown in any repo you've linked and
                          your backlog — this can take a moment…
                        </div>
                      </Show>
                    </div>
                  }
                >
                  {(row) => (
                    <Show
                      when={!row().committed_at}
                      fallback={
                        <>
                          <CommittedItemsSummary
                            breakdown={row()}
                            summary={committedSummary(row().intake_id)}
                          />
                          <CapturedDecisionsPanel
                            decisions={decisions.decisions()}
                            checked={decisions.checked()}
                            extracting={decisions.extracting()}
                            error={decisions.error()}
                            projectId={projectId()}
                            onConfirm={(id) =>
                              decisions.confirm(projectId(), id)
                            }
                            onDismiss={(id) =>
                              void decisions.dismiss(projectId(), id)
                            }
                            onClearError={decisions.clearError}
                          />
                        </>
                      }
                    >
                      <div class="mt-8 overflow-hidden rounded-md border border-line bg-surface lg:grid lg:grid-cols-[1fr_380px] lg:divide-x lg:divide-line">
                        <BreakdownPanel
                          onDiscard={() => setAbandoning("discard")}
                          breakdown={row()}
                          rejectedStoryIds={breakdown.rejectedStoryIds()}
                          onToggleStory={breakdown.toggleStory}
                          approvedCount={breakdown.approvedCount()}
                          totalEstimate={breakdown.totalEstimate()}
                          busy={breakdown.busy()}
                          onCommit={async () => {
                            const result = await breakdown.commit();
                            if (result) {
                              setCommittedSummary(row().intake_id, {
                                epics: result.epics.map((epic) => ({
                                  id: epic.id,
                                  item_number: epic.item_number,
                                  title: epic.title,
                                })),
                                stories: result.stories.map((story) => ({
                                  id: story.id,
                                  item_number: story.item_number,
                                  title: story.title,
                                })),
                              });
                            }
                          }}
                        />
                        <div class="border-t border-line lg:border-t-0">
                          <NegotiationThread
                            messages={breakdown.messages()}
                            pending={breakdown.pending()}
                            busy={breakdown.busy()}
                            onSubmit={breakdown.negotiate}
                          />
                        </div>
                      </div>
                    </Show>
                  )}
                </Show>
              </Show>

              <div class="mt-6 text-center">
                <Button variant="secondary" size="sm" onClick={startAnother}>
                  Start another initiative
                </Button>
              </div>
            </Match>

            {/* Clarifying — the guided question thread */}
            <Match when={intake.intake()}>
              <IntakeThread
                messages={intake.messages()}
                questions={questions()}
                pending={intake.pending()}
                busy={intake.busy()}
              />

              <div class="mt-8">
                <IntakeComposer
                  autofocus
                  disabled={intake.busy()}
                  placeholder="Your answer…"
                  aria-label="Answer the clarifying question"
                  onSubmit={intake.answer}
                />
                <div class="mt-2 text-right">
                  <button
                    type="button"
                    class="text-sm text-ink-3 hover:text-ink-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={intake.busy()}
                    onClick={() => void intake.skip()}
                  >
                    Skip the rest, just go →
                  </button>
                </div>
              </div>
            </Match>
          </Switch>

          <DecisionQuickCapture projectId={projectId()} />

          <Show when={abandoning()}>
            {(action) => (
              <Modal
                open
                size="sm"
                title={ABANDON_COPY[action()].title}
                onClose={() => setAbandoning(null)}
              >
                <p class="text-sm text-ink-2">{ABANDON_COPY[action()].body}</p>
                <div class="mt-4 flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setAbandoning(null)}
                  >
                    {ABANDON_COPY[action()].cancel}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => void confirmAbandon()}
                  >
                    {ABANDON_COPY[action()].confirm}
                  </Button>
                </div>
              </Modal>
            )}
          </Show>
        </div>
      </main>
    </div>
  );
};

export default PlanPage;
